<script lang="ts" generics="T extends GoalType | TaskType">
  import Honeybadger from "$app/utils/honeybadger"
  import { ChevronDown } from "lucide-svelte"
  import { xhr, toast } from "$app/utils"
  import Dropdown from "$app/components/Dropdown.svelte"
  import { STATUSES } from "$app/constants"

  interface Props {
    goalOrTask: T
    id?: string
    type?: "Goal" | "Task"
    show_border?: boolean
    small?: boolean
    editable?: boolean
    onupdate: (data: T) => void
    update?: boolean
    dataName?: string
  }

  let {
    goalOrTask = $bindable(),
    type = "Goal",
    id = "status",
    show_border = true,
    small = false,
    editable = false,
    onupdate,
    update = true,
    dataName = "goal-status"
  }: Props = $props()

  let openGoalStatusDropdown = $state(false)

  function getUrl(): string {
    if (type === "Task") {
      return `/plans/${goalOrTask.plan_id}/tasks/${goalOrTask.id}.json`
    } else {
      return `/plans/${goalOrTask.plan_id}/goals/${goalOrTask.id}.json`
    }
  }

  let saving = $state(false)
  async function handleSave(value: StatusType): Promise<void> {
    if (!update) {
      onupdate({ ...goalOrTask, status: value })
      openGoalStatusDropdown = false
      return
    }
    saving = true
    openGoalStatusDropdown = false
    let data = {
      [type.toLowerCase()]: {
        status: value
      }
    }
    let url = getUrl()

    try {
      const resp = await xhr.patch<T>(url, data)
      goalOrTask = { ...goalOrTask, ...resp.data }

      onupdate(resp.data)

      openGoalStatusDropdown = false
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error saving the status", "error")
    } finally {
      saving = false
    }
  }
</script>

{#snippet status(status: StatusType | undefined)}
  {#if status}
    {@const item = STATUSES[status] || { label: "Unknown", class: "" }}
    <div class="flex items-center gap-0.5 w-18">
      <div class={["flex-none size-3 rounded-full", `bg-${item.class}`]}></div>
      <div
        class="ml-1 flex flex-row items-center justify-start whitespace-nowrap capitalize text-sm"
      >
        {item.label}
      </div>
    </div>
  {:else}
    <div class="text-sm">Not Set</div>
  {/if}
{/snippet}

{#if editable}
  <Dropdown closeOnOuterClick={true} bind:open={openGoalStatusDropdown}>
    {#snippet button()}
      <div
        class={[
          "flex items-center gap-1 px-1.5 py-2 justify-between border-gray-200 rounded shadow-sm border text-sm edit-status",
          small ? "w-28 py-1" : "w-36 py-2",
          show_border ? "btn-outline" : ""
        ]}
        {id}
        data-name={dataName}
      >
        {@render status(goalOrTask.status)}

        <ChevronDown class="size-4 text-gray-700" />
      </div>
    {/snippet}
    <div>
      <div
        class={[
          "origin-top-left right-0 absolute mt-2 rounded-md shadow bg-white ring-1 ring-gray-300 ring-opacity-5 focus:outline-none z-30",
          small ? "w-28" : "w-36"
        ]}
        role="menu"
      >
        <div class="py-1">
          {#each Object.keys(STATUSES) as StatusType[] as key (key)}
            {@const st = STATUSES[key]}
            <button
              onclick={(e) => handleSave(key)}
              class="btn-dropdown"
              type="button"
            >
              {@render status(key)}
            </button>
          {/each}
        </div>
      </div>
    </div>
  </Dropdown>
{:else}
  <button
    class:btn-outline={show_border}
    class={[
      "mt-1 mr-2 px-2 py-2 flex justify-start border-gray-200 rounded shadow-sm border text-sm items-center",
      small ? "w-28 py-1" : "mt-1 w-36 py-2"
    ]}
    {id}
    data-name={dataName}
  >
    {@render status(goalOrTask.status)}
  </button>
{/if}
